// client/src/pages/InviteJoin.js
import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { apiFetch } from '../lib/api';
import logo from '../assets/chitconnect-logo-blue.png';

export default function InviteJoin() {
  const { token } = useParams();
  const navigate = useNavigate();

  const [info, setInfo] = useState(null);
  const [memberships, setMemberships] = useState(1);
  const [handle, setHandle] = useState('');
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState('');

  const isLoggedIn = useMemo(() => {
    try { return !!sessionStorage.getItem('user'); } catch { return false; }
  }, []);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      setMsg('');
      try {
        const res = await apiFetch(`/api/invitations/info/${token}`);
        const data = await res.json().catch(() => ({}));
        if (!alive) return;

        if (!res.ok) {
          setMsg(data?.error || `Failed to load invite (HTTP ${res.status})`);
          setInfo(null);
        } else {
          setInfo(data);
        }
      } catch (e) {
        if (alive) setMsg('Server error while loading invite.');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [token]);

  const stashInvite = (prefill) => {
    try {
      sessionStorage.setItem('pending_invite', token);
      sessionStorage.setItem('pending_invite_memberships', String(Math.max(1, Number(memberships) || 1)));
      if (prefill) sessionStorage.setItem('prefill_handle', String(prefill));
    } catch {}
  };

  const continueToLoginOrRegister = async () => {
    setMsg('');
    const raw = String(handle || '').trim();
    if (!raw) {
      setMsg('❌ Please enter your email or mobile to continue.');
      return;
    }

    try {
      const res = await apiFetch(`/api/users/exists?handle=${encodeURIComponent(raw)}`);
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setMsg(data?.error || `Failed to check account (HTTP ${res.status})`);
        return;
      }

      stashInvite(raw);
      if (data?.exists) navigate('/login');
      else navigate('/register');
    } catch {
      setMsg('Server error while checking your account.');
    }
  };

  const accept = async (e) => {
    e.preventDefault();
    setMsg('');
    try {
      const res = await apiFetch('/api/invitations/accept', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token,
          requested_memberships: Math.max(1, Number(memberships) || 1),
        }),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        // If not logged in, bounce to login
        if (res.status === 401) {
          setMsg('❌ Please log in to accept this invitation.');
          stashInvite();
          navigate('/login');
          return;
        }
        setMsg(data?.error || `Failed to accept invite (HTTP ${res.status})`);
        return;
      }

      const gid = data?.group_id || info?.group_id;
      setMsg('✅ Join request sent. Pending organiser approval.');
      try { toast.info('Join request sent. Awaiting organiser approval.'); } catch {}
      setTimeout(() => {
        navigate('/');
      }, 600);
} catch {
      setMsg('Server error while accepting invite.');
    }
  };

  const blockedOwner = !!info?.is_owner;

  return (
    <div className="main-content">
      <img src={logo} alt="ChitConnect Logo" className="group-logo" />

      <div className="card">
        <h2>Group Invitation</h2>

        {loading && <p>Loading…</p>}

        {!loading && info && (
          <>
            <p><strong>Group:</strong> {info.group_name}</p>
            {info.description && <p><strong>Description:</strong> {info.description}</p>}
            {typeof info.monthly_contribution !== 'undefined' && (
              <p><strong>Contribution:</strong> ${info.monthly_contribution}</p>
            )}

            {blockedOwner ? (
              <div className="error-text" style={{ marginTop: 12 }}>
                ❌ You are the organiser of this group. Invitations are only for members.
              </div>
            ) : (
              <form onSubmit={accept} style={{ marginTop: 12 }}>
                <label style={{ display: 'block', marginBottom: 8 }}>
                  Number of memberships
                  <input
                    type="number"
                    min={1}
                    value={memberships}
                    onChange={(e) => setMemberships(e.target.value)}
                    style={{ width: '100%', marginTop: 6 }}
                  />
                </label>

                {!isLoggedIn ? (
                  <>
                    <label style={{ display: 'block', marginBottom: 8 }}>
                      Email or mobile
                      <input
                        type="text"
                        value={handle}
                        onChange={(e) => setHandle(e.target.value)}
                        placeholder="e.g. alice@site.com or +61412345678"
                        style={{ width: '100%', marginTop: 6 }}
                        autoComplete="username"
                      />
                    </label>

                    <button type="button" className="action-button" onClick={continueToLoginOrRegister}>
                      Continue
                    </button>

                    <div style={{ marginTop: 10, fontSize: 13, color: 'var(--cc-muted)' }}>
                      If you already have an account you’ll be taken to <strong>Login</strong>.
                      Otherwise you’ll be taken to <strong>Register</strong>.
                    </div>
                  </>
                ) : (
                  <button type="submit" className="action-button">
                    Accept invitation
                  </button>
                )}
              </form>
            )}
          </>
        )}

        {!loading && !info && msg && (
          <p className="error-text">{msg}</p>
        )}

        {msg && info && (
          <p className={msg.startsWith('✅') ? 'success-text' : 'error-text'} style={{ marginTop: 12 }}>
            {msg}
          </p>
        )}
      </div>
    </div>
  );
}
